import React, { FunctionComponent } from 'react'
import NumberFormat from 'react-number-format'
import { InputBaseComponentProps } from '@mui/material'
import Slider from '@mui/material/Slider'
import { useFormik } from 'formik'
import * as yup from 'yup'

import Select from 'components/Common/Select'
import ColorBox from 'components/Inspection/Creation/VehicleInfo/ColorBox'
import ButtonContainer from 'components/Template/Creation/ButtonContainer'

import { maxMileage } from 'constants/car'
import { VEHICLE_CREATION_OPTION } from 'constants/Inventory/creation'
import { textFiles } from 'constants/textFiles'
import useTranslation from 'hooks/useTranslation'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { SecondStepModel } from 'models/services/PreInventory/creation'
import { categoriesWidth, VehicleCreationProps } from 'utils/Inventory/creation'

import {
  BoxContainer,
  sliderFormProps,
  StyledContainer,
  StyledErrorMessage,
  StyledForm,
  StyledTextField,
  StyledTextFieldContainer,
} from 'styles/creation'

import MultipleSelect from '../../Common/MultipleSelect'

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

const NumberFormatCustom = React.forwardRef<NumberFormat<number>, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props

    return (
      <NumberFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          })
        }}
        thousandSeparator
        isNumericString
        allowNegative={false}
        max={maxMileage}
      />
    )
  }
)

const VehicleDetail = ({
  vehicleData,
  updateVehicleData,
  handleSecondaryBack,
  handleSecondaryContinue,
  vehicleSelectOptions,
  colorList,
  countryVersionList,
}: VehicleCreationProps) => {
  const { width } = useWindowDimensions()
  const {
    text: {
      subSteps: { secondStep: translation },
    },
  } = useTranslation(textFiles.VEHICLE_CREATION)
  const { text: validationText } = useTranslation(textFiles.VALIDATION)

  const validationSchema = yup.object().shape({
    vinNumber: yup
      .string()
      .length(17, validationText.errorMessageRequiredLength.replace('%d', 17)),
    chassisNumber: yup
      .string()
      .min(6, validationText.errorMessageAtleast.replace('%d', 6))
      .max(10, validationText.errorMessageMaxCharacters.replace('%d', 11))
      .when('vinNumber', {
        is: (vinNumber: string) => !vinNumber || vinNumber.length === 0,
        then: yup.string().required(translation.vinChassisValidation),
        otherwise: yup.string(),
      }),
    mileage: yup.string().required(validationText.fieldRequired),
    licensePlate: yup.string().required(validationText.fieldRequired),
    categories: yup
      .array()
      .of(yup.string())
      .min(1, validationText.errorRequiredItems.replace('%d', 1)),
    countryVersion: yup.string().required(validationText.fieldRequired),
    exteriorColor: yup.string().required(validationText.fieldRequired),
    interiorColor: yup.string().required(validationText.fieldRequired),
  })

  const formik = useFormik<SecondStepModel>({
    initialValues: vehicleData.vehicleInformation.secondStep,
    validationSchema,
    onSubmit: (values) => {
      updateVehicleData({
        type: VEHICLE_CREATION_OPTION.UPDATE_VEHICLE_INFORMATION,
        payload: {
          ...vehicleData.vehicleInformation,
          secondStep: values,
        },
      })
      handleSecondaryContinue()
    },
  })

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    formik.setFieldValue('mileage', String(newValue))
  }

  const handleBlur = () => {
    if (Number(formik.values.mileage) > maxMileage) {
      formik.setFieldValue('mileage', String(maxMileage))
    }
  }

  const getColor = (colorId: string) => {
    const color = colorList.find(
      (currentColor) => currentColor.value === colorId
    )

    return color ? color.hexCode : ''
  }

  const handleCategoryRemove = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    const newCategories = [...formik.values.categories]
    newCategories.splice(index, 1)
    formik.setFieldValue('categories', newCategories)
  }

  return (
    <StyledContainer>
      <StyledForm
        sx={{
          ...sliderFormProps,
          maxWidth: width ? `${categoriesWidth(width)} !important` : '100%',
        }}
        onSubmit={formik.handleSubmit}
      >
        <div>
          <StyledTextFieldContainer title={translation.vehicleMileage}>
            <BoxContainer sx={{ paddingLeft: '20px' }}>
              <Slider
                value={Number(formik.values.mileage)}
                onChange={handleSliderChange}
                max={maxMileage}
                step={100}
                aria-labelledby="input-slider"
                sx={{
                  width: '70%',
                }}
              />
              <StyledTextField
                variant="outlined"
                name="mileage"
                value={formik.values.mileage}
                onChange={formik.handleChange}
                onBlur={handleBlur}
                InputProps={{
                  inputComponent:
                    NumberFormatCustom as unknown as FunctionComponent<InputBaseComponentProps>,
                }}
                sx={{
                  width: '25% !important',
                }}
              />
            </BoxContainer>
            {formik.touched.mileage && formik.errors.mileage ? (
              <StyledErrorMessage text={formik.errors.mileage} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer title={translation.countryVersion}>
            <Select
              options={countryVersionList}
              onChange={formik.handleChange}
              label={translation.countryVersion}
              value={formik.values.countryVersion}
              name="countryVersion"
              sx={{ fontSize: 12, height: 40 }}
              error={
                formik.touched.countryVersion &&
                Boolean(formik.errors.countryVersion)
              }
            />
            {formik.touched.countryVersion && formik.errors.countryVersion ? (
              <StyledErrorMessage text={formik.errors.countryVersion} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer title={translation.interiorColor}>
            <BoxContainer>
              <Select
                options={colorList}
                onChange={formik.handleChange}
                label={translation.interiorColor}
                value={formik.values.interiorColor}
                name="interiorColor"
                sx={{ fontSize: 12, height: 40 }}
                error={
                  formik.touched.interiorColor &&
                  Boolean(formik.errors.interiorColor)
                }
              />
              <ColorBox code={getColor(formik.values.interiorColor)} />
            </BoxContainer>
            {formik.touched.interiorColor && formik.errors.interiorColor ? (
              <StyledErrorMessage text={formik.errors.interiorColor} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer title={translation.exteriorColor}>
            <BoxContainer>
              <Select
                options={colorList}
                onChange={formik.handleChange}
                label={translation.exteriorColor}
                value={formik.values.exteriorColor}
                name="exteriorColor"
                sx={{ fontSize: 12, height: 40 }}
                error={
                  formik.touched.exteriorColor &&
                  Boolean(formik.errors.exteriorColor)
                }
              />
              <ColorBox code={getColor(formik.values.exteriorColor)} />
            </BoxContainer>
            {formik.touched.exteriorColor && formik.errors.exteriorColor ? (
              <StyledErrorMessage text={formik.errors.exteriorColor} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer title={translation.chassisNumber}>
            <StyledTextField
              variant="outlined"
              name="chassisNumber"
              value={formik.values.chassisNumber}
              placeholder={translation.chassisNumber}
              onChange={formik.handleChange}
              error={
                formik.touched.chassisNumber &&
                Boolean(formik.errors.chassisNumber)
              }
            />
            {formik.touched.chassisNumber && formik.errors.chassisNumber ? (
              <StyledErrorMessage text={formik.errors.chassisNumber} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer
            sx={{ maxWidth: '280px !important' }}
            title={translation.vinNumber}
          >
            <StyledTextField
              variant="outlined"
              name="vinNumber"
              value={formik.values.vinNumber}
              placeholder={translation.vinNumber}
              onChange={formik.handleChange}
              fullWidth
              error={
                formik.touched.vinNumber && Boolean(formik.errors.vinNumber)
              }
            />
            {formik.touched.vinNumber && formik.errors.vinNumber ? (
              <StyledErrorMessage text={formik.errors.vinNumber} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer title={translation.licensePlate}>
            <StyledTextField
              variant="outlined"
              name="licensePlate"
              value={formik.values.licensePlate}
              placeholder={translation.licensePlate}
              onChange={formik.handleChange}
              error={
                formik.touched.licensePlate &&
                Boolean(formik.errors.licensePlate)
              }
            />
            {formik.touched.licensePlate && formik.errors.licensePlate ? (
              <StyledErrorMessage text={formik.errors.licensePlate} />
            ) : null}
          </StyledTextFieldContainer>

          <StyledTextFieldContainer
            sx={{ flexGrow: 1, maxWidth: '100% !important' }}
            title={translation.category}
          >
            <MultipleSelect
              options={vehicleSelectOptions.categories}
              onChange={formik.handleChange}
              label={translation.category}
              values={formik.values.categories}
              name="categories"
              error={
                formik.touched.categories && Boolean(formik.errors.categories)
              }
              onRemove={handleCategoryRemove}
            />
            {formik.touched.categories && formik.errors.categories ? (
              <StyledErrorMessage
                text={
                  typeof formik.errors.categories === 'string'
                    ? formik.errors.categories
                    : ''
                }
              />
            ) : null}
          </StyledTextFieldContainer>
        </div>
      </StyledForm>
      <ButtonContainer
        previousFunction={handleSecondaryBack}
        nextFunction={formik.handleSubmit}
      />
    </StyledContainer>
  )
}

export default VehicleDetail
